/**
 * Service account JSON upload and key details display for GCP Vertex AI discovery form.
 */

import { Button, Icon, Typography } from "@saviynt/design-system";
import { useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";

/** Parsed fields from uploaded service account JSON (for display only) */
export interface ServiceAccountKeyMetadata {
  type: string;
  project_id: string;
  private_key_id: string;
  client_email: string;
  client_id: string;
}

export interface ServiceAccountKeyUploadProps {
  content: string;
  fileName: string;
  metadata: ServiceAccountKeyMetadata | null;
  onFileSelected: (
    content: string,
    fileName: string,
    metadata: ServiceAccountKeyMetadata | null,
  ) => void;
  onRemove: () => void;
  inputId?: string;
}

function parseMetadata(
  parsed: Record<string, unknown>,
): ServiceAccountKeyMetadata {
  return {
    type: String(parsed.type ?? ""),
    project_id: String(parsed.project_id ?? ""),
    private_key_id: String(parsed.private_key_id ?? ""),
    client_email: String(parsed.client_email ?? ""),
    client_id: String(parsed.client_id ?? ""),
  };
}

export function ServiceAccountKeyUpload({
  content,
  fileName,
  metadata,
  onFileSelected,
  onRemove,
  inputId = "google-vertex-service-key",
}: ServiceAccountKeyUploadProps) {
  const intl = useIntl();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasKey = content.length > 0;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".json") && file.type !== "application/json") {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      try {
        const parsed = JSON.parse(text) as Record<string, unknown>;
        onFileSelected(text, file.name, parseMetadata(parsed));
      } catch {
        onFileSelected("", "", null);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex flex-col gap-1">
      <Typography kind="label" className="block" htmlFor={inputId}>
        <FormattedMessage
          id="ZddiFZ"
          defaultMessage="Service Account Key"
          description="Service account key upload label"
        />
        <span className="text-[var(--color-foreground-critical-medium)] ml-0.5">
          <FormattedMessage
            id="+uGQkt"
            defaultMessage="*"
            description="Required field indicator"
          />
        </span>
      </Typography>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        onChange={handleFileChange}
        className="hidden"
        id={inputId}
        aria-label={intl.formatMessage({
          id: "DdbGeg",
          defaultMessage: "Upload JSON file",
          description: "Upload button for service account key",
        })}
      />
      {!hasKey ? (
        <Button
          kind="outlined"
          size="medium"
          onClick={handleUploadClick}
          leftIcon={<Icon kind="Upload" size="small" />}
        >
          <FormattedMessage
            id="DdbGeg"
            defaultMessage="Upload JSON file"
            description="Upload button for service account key"
          />
        </Button>
      ) : (
        <div
          className="flex items-center gap-2 p-3 rounded border"
          style={{
            borderColor: "var(--color-border-secondary-subtlest, #293950)",
            backgroundColor: "var(--color-background-secondary-subtlest)",
          }}
        >
          <Icon kind="Document" size="small" />
          <span
            className="flex-1 truncate text-[var(--color-foreground-neutral-medium)]"
            title={fileName}
          >
            {fileName}
          </span>
          <Button
            kind="ghost"
            size="small"
            onClick={onRemove}
            aria-label={intl.formatMessage({
              id: "ITY0Wh",
              defaultMessage: "Remove file",
              description: "Remove uploaded file button aria-label",
            })}
          >
            <FormattedMessage
              id="dorHAE"
              defaultMessage="Remove"
              description="Remove uploaded file"
            />
          </Button>
        </div>
      )}
      <Typography
        kind="body2"
        className="text-[var(--color-foreground-neutral-subtle)]"
      >
        <FormattedMessage
          id="PxdUfq"
          defaultMessage="Upload a JSON key file from your Google Cloud service account."
          description="Hint for service account key upload"
        />
      </Typography>

      {hasKey && metadata && (
        <div
          className="mt-3 p-4 rounded border flex flex-col gap-2"
          style={{
            borderColor: "var(--color-border-secondary-subtlest, #293950)",
            backgroundColor: "var(--color-background-secondary-subtlest)",
          }}
          data-testid="service-account-key-details"
        >
          <Typography
            kind="body2-bold"
            className="text-[var(--color-foreground-neutral-bold)]"
          >
            <FormattedMessage
              id="ZOovuO"
              defaultMessage="Key details"
              description="Heading for decoded service account key details"
            />
          </Typography>
          <div className="grid grid-cols-1 gap-2">
            <div className="flex flex-wrap gap-x-2">
              <Typography
                kind="body2"
                className="text-[var(--color-foreground-neutral-subtle)] shrink-0"
              >
                <FormattedMessage
                  id="R8zcTr"
                  defaultMessage="Account Type:"
                  description="Label for account type"
                />
              </Typography>
              <Typography
                kind="body2"
                className="text-[var(--color-foreground-neutral-default)] break-all"
              >
                {metadata.type ||
                  intl.formatMessage({
                    id: "HcBT8U",
                    defaultMessage: "—",
                    description: "Empty value placeholder",
                  })}
              </Typography>
            </div>
            <div className="flex flex-wrap gap-x-2">
              <Typography
                kind="body2"
                className="text-[var(--color-foreground-neutral-subtle)] shrink-0"
              >
                <FormattedMessage
                  id="YefP9P"
                  defaultMessage="Project ID:"
                  description="Label for project ID from key"
                />
              </Typography>
              <Typography
                kind="body2"
                className="text-[var(--color-foreground-neutral-default)] break-all"
              >
                {metadata.project_id ||
                  intl.formatMessage({
                    id: "HcBT8U",
                    defaultMessage: "—",
                    description: "Empty value placeholder",
                  })}
              </Typography>
            </div>
            <div className="flex flex-wrap gap-x-2">
              <Typography
                kind="body2"
                className="text-[var(--color-foreground-neutral-subtle)] shrink-0"
              >
                <FormattedMessage
                  id="THhn+G"
                  defaultMessage="Private Key ID:"
                  description="Label for private key ID"
                />
              </Typography>
              <Typography
                kind="body2"
                className="text-[var(--color-foreground-neutral-default)] break-all font-mono text-xs"
              >
                {metadata.private_key_id ||
                  intl.formatMessage({
                    id: "HcBT8U",
                    defaultMessage: "—",
                    description: "Empty value placeholder",
                  })}
              </Typography>
            </div>
            <div className="flex flex-wrap gap-x-2">
              <Typography
                kind="body2"
                className="text-[var(--color-foreground-neutral-subtle)] shrink-0"
              >
                <FormattedMessage
                  id="+SVbiv"
                  defaultMessage="Client Email:"
                  description="Label for client email"
                />
              </Typography>
              <Typography
                kind="body2"
                className="text-[var(--color-foreground-neutral-default)] break-all"
              >
                {metadata.client_email ||
                  intl.formatMessage({
                    id: "HcBT8U",
                    defaultMessage: "—",
                    description: "Empty value placeholder",
                  })}
              </Typography>
            </div>
            <div className="flex flex-wrap gap-x-2">
              <Typography
                kind="body2"
                className="text-[var(--color-foreground-neutral-subtle)] shrink-0"
              >
                <FormattedMessage
                  id="iw8Qp5"
                  defaultMessage="Client ID:"
                  description="Label for client ID"
                />
              </Typography>
              <Typography
                kind="body2"
                className="text-[var(--color-foreground-neutral-default)] break-all"
              >
                {metadata.client_id ||
                  intl.formatMessage({
                    id: "HcBT8U",
                    defaultMessage: "—",
                    description: "Empty value placeholder",
                  })}
              </Typography>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
